import React, { useState, useEffect } from 'react'
import { CircularProgress, Typography, withStyles } from '@material-ui/core'
import styles from './styles'
import data, { settingItemListTypeEnum, settingItemTypeEnum } from './data'
import clsx from 'clsx'
import { track } from '@amplitude/analytics-browser'
import {
  CoreAutoComplete,
  CoreButton,
  CoreIconButton,
} from 'src/components/core'
import { Icon } from '@iconify/react'

const Settings = props => {
  const { classes, settingsChangeHandler } = props

  const [settingList, setSettingList] = useState([])
  const [loading, setLoading] = useState(false)

  const [selectedSettings, setSelectedSettings] = useState([])

  useEffect(() => {
    settingsChangeHandler(selectedSettings)
  }, [selectedSettings])

  const handleClickOnSettingAddButton = async _category => {
    if (!_category) {
      console.error('Category is undefined or null')
      return
    }

    try {
      const category = { ..._category } // Clone the category object to ensure immutability

      // Track the action
      track('creation_setting_selected', { creation_setting: category.title })

      // Dynamically execute listFunc if the listType is DYNAMIC
      if (
        category.settingItems &&
        category.settingItems[0] &&
        category.settingItems[0].listType === settingItemListTypeEnum.DYNAMIC &&
        typeof category.settingItems[0].listFunc === 'function'
      ) {
        setLoading(true)
        // Fetch the dynamic list and resolve the Promise
        const list = await category.settingItems[0].listFunc()

        // Update the first item immutably
        category.settingItems = category.settingItems.map((item, index) =>
          index === 0 ? { ...item, list } : item,
        )
        setLoading(false)
      }

      // Update state immutably with the resolved category
      setSettingList(prevList => [...prevList, category])
    } catch (error) {
      console.error('Error handling setting addition:', error)
    }
  }

  const removeItem = item => {
    let newSettingList = settingList

    if (item && item.removeItems) {
      // Remove items from the setting list
      newSettingList = settingList.map(items => {
        return {
          ...items,
          settingItems: items.settingItems.filter(
            settingItem => !item.removeItems.includes(settingItem.key),
          ),
        }
      })
    }

    newSettingList = newSettingList.map(setting => {
      return {
        ...setting,
        settingItems: setting.settingItems.filter(x => x.key != item.key),
      }
    })

    newSettingList = newSettingList.filter(x => x.settingItems.length > 0)

    setSettingList(newSettingList)

    let newSelectedSettings = selectedSettings.filter(x => x.key != item.key)
    setSelectedSettings(newSelectedSettings)
  }

  const addItemsToSettingList = (settingList, addItemsKeys, data) => {
    // Loop through addItemsKeys to find the corresponding settingItems
    addItemsKeys.forEach(keyToAdd => {
      data.forEach(section => {
        section.settingItems.forEach(settingItem => {
          if (settingItem.key === keyToAdd) {
            // Check if the item is already in the settingList to avoid duplicates
            const isAlreadyAdded = settingList.some(existingSection =>
              existingSection.settingItems.some(
                existingItem => existingItem.key === settingItem.key,
              ),
            )

            if (!isAlreadyAdded) {
              // Find or create the section in the settingList
              let sectionInSettingList = settingList.find(
                existingSection => existingSection.type === section.type,
              )
              if (!sectionInSettingList) {
                sectionInSettingList = {
                  type: section.type,
                  title: section.title,
                  settingItems: [],
                }
                settingList.push(sectionInSettingList)
              }

              // Add the item to the section
              sectionInSettingList.settingItems.push(settingItem)
            }
          }
        })
      })
    })

    return settingList
  }

  const onChangeHandler = (item, value) => {
    let newSelectedSettings = selectedSettings.filter(x => x.key != item.key)

    let selectedOption = item.list.filter(x => x.value == value)[0]

    if (selectedOption && selectedOption.removeItems) {
      // Remove items from the setting list
      const newSettingList = settingList.map(items => {
        return {
          ...items,
          settingItems: items.settingItems.filter(
            settingItem =>
              !selectedOption.removeItems.includes(settingItem.key),
          ),
        }
      })

      setSettingList(newSettingList)
    }

    if (selectedOption && selectedOption.addItems) {
      // Add items from the setting list
      const newSettingList = addItemsToSettingList(
        settingList,
        selectedOption.addItems,
        data,
      )

      setSettingList(newSettingList)
    }

    // Ensure quiz and survey counts are always numbers
    let processedValue = value
    if (item.key === 'finalQuizCount' || item.key === 'finalSurveyCount') {
      processedValue = parseInt(value, 10)
      // Handle NaN case - fallback to 0
      if (isNaN(processedValue)) {
        processedValue = 0
      }
    }

    let newSetting = { key: item.key, value: processedValue }

    setSelectedSettings([...newSelectedSettings, newSetting])
  }

  const renderSettingItem = (item, index) => {
    let list = item.list

    return (
      <div key={item.key} className={classes.root_content}>
        <div
          className={clsx({
            [classes.root_content_text]: true,
            [classes.root_content_text_column]:
              item.type == settingItemTypeEnum.TEXTFIELD,
            [classes.root_content_text_row]:
              item.type == settingItemTypeEnum.SELECT,
          })}
        >
          <Typography className={classes.root_content_text_title}>
            {index}. {item.description} {item.freeSolo}
          </Typography>
          <div
            style={{
              flexGrow: 1,
            }}
          ></div>
          <div
            className={clsx({
              [classes.auto_complete]: true,
              [classes.auto_complete_max_width]:
                item.type == settingItemTypeEnum.SELECT,
            })}
          >
            <CoreAutoComplete
              marginTop={false}
              defaultValue={item.defaultValue}
              freeSolo={item.freeSolo == false ? false : true}
              selectOnFocus
              options={list}
              onInputChange={(event, value) => {
                if (value === '' && event.type == 'click') {
                  removeItem(item)
                }
              }}
              onChange={(event, value) => {
                onChangeHandler(
                  item,
                  value != null ? value.value : event.target.value,
                )
              }}
              onBlur={(event, value) => {
                if (list.find(x => x.name == event.target.value) == undefined) {
                  onChangeHandler(
                    item,
                    value != null ? value.value : event.target.value,
                  )
                }
              }}
              id={'auto_complete_' + item.key}
              key={'auto_complete_' + item.key}
            />
            <div className={classes.icon_button_container}>
              <CoreIconButton
                color="dark"
                size="small"
                variant="text"
                onClick={() => removeItem(item)}
              >
                <Icon icon="iconamoon:close-light" />
              </CoreIconButton>
            </div>
          </div>
        </div>
      </div>
    )
  }
  let indexOfSelectedSetting = 0
  return (
    <>
      <Typography fontWeight={700}>Customization</Typography>
      <div className={classes.root}>
        {settingList.length > 0 &&
          settingList.map(items => {
            return items.settingItems.map(item => {
              indexOfSelectedSetting++
              return renderSettingItem(item, indexOfSelectedSetting)
            })
          })}

        {settingList.length > 0 && <div className={classes.divider}></div>}

        {data
          .filter(
            item => settingList.filter(x => x.type == item.type).length == 0,
          )
          .map(item => (
            <CoreButton
              onClick={() => {
                handleClickOnSettingAddButton(item)
              }}
              startIcon={
                loading &&
                item.settingItems[0]?.listType ===
                  settingItemListTypeEnum.DYNAMIC ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon icon="uil:plus" />
                )
              }
              variant="outlined"
              color="secondary"
              size="medium"
              disabled={
                loading &&
                item.settingItems[0]?.listType ===
                  settingItemListTypeEnum.DYNAMIC
              }
            >
              {item.title}
            </CoreButton>
          ))}
      </div>
    </>
  )
}

export default withStyles(styles)(Settings)
